import { ChangeEvent, useEffect, useState } from 'react';
import { TextField } from '@mui/material';
import { Control, Controller, FieldErrors } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  issuePblLawyerPhoneOtp,
  issuePblLawyerApplicationEmailVerification,
  verifyPblLawyerPhoneOtp,
  verifyPblLawyerApplicationEmailVerification,
} from '@/api/pbl.api';
import { Button } from '@/components/UI/Button';
import { pblLanguages, pblProfessionalQualifications } from '@/data/pbl';
import { Box, Flex } from '@/layout';
import { errorHandler } from '@/utils/toastHandler';
import { authRequestError } from '@/utils/authRequestError';
import { InputWrapper, FormTitle, MultiInputWrapper } from '@/components/Container/AuthContainer/styles';

import type { UpdateVolunteerForm, VolunteerFormState } from '../types';
import { requiresOtherProfessionalQualificationText } from '../formState';
import { FormPanel, FormSectionTitle, IconWrapper, OtpStatusText, SectionTitle, ValidationText } from '../styles';
import { ToggleGroup } from '../shared';
import { Visible } from '@/components/Icon';
import { Text } from '@/components/UI/Typography/Text';
import { theme } from '@/theme/Theme';

interface Props {
  form: VolunteerFormState;
  validationError: string;
  errors: FieldErrors<VolunteerFormState>;
  control: Control<VolunteerFormState>;
  isResubmission?: boolean;
  isMobile: boolean;
  isTablet: boolean;
  updateForm: UpdateVolunteerForm;
}

type OtpChannel = 'phone' | 'email';
type OtpStatus = {
  tone: 'info' | 'success' | 'error';
  message: string;
};

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phonePattern = /^[+\d][\d\s-]{7,19}$/;
const smsPhoneValidationMessage = 'SMS phone number must be 8 digits and start with 4, 5, 6, 7, 8 or 9.';
const OTP_REQUEST_COOLDOWN_SECONDS = 60;

const getOtpFieldName = (channel: OtpChannel) => (channel === 'phone' ? 'phone_otp' : 'email_otp');

const normalizeHongKongMobile = (value: string) => {
  const compactValue = value.replace(/[\s-]/g, '').trim();
  let phone = compactValue;

  if (phone.startsWith('+852')) {
    phone = phone.slice(4);
  } else if (phone.startsWith('852') && phone.length === 11) {
    phone = phone.slice(3);
  }

  if (!/^[4-9]\d{7}$/.test(phone)) {
    return null;
  }

  return {
    phone_country_code: '+852' as const,
    phone,
  };
};

const getApiErrorMessage = (error: any, fallback: string) =>
  error.response?.data?.message || error.response?.data?.data || error.message || fallback;

const PersonalInformationStep = ({
  form,
  validationError,
  errors,
  control,
  isResubmission = false,
  isMobile,
  isTablet,
  updateForm,
}: Props) => {
  //   @ts-ignore
  const { t } = useTranslation();
  const showQualificationOther = requiresOtherProfessionalQualificationText(form);
  const showLanguageOther = form.language_proficiency?.includes('other');
  const showAccountFields = !isResubmission;
  const admissionQuestionNumber = isResubmission ? '2.' : '4.';
  const qualificationQuestionNumber = isResubmission ? '3.' : '5.';
  const languageQuestionNumber = isResubmission ? '4.' : '6.';
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [otpIssued, setOtpIssued] = useState<Record<OtpChannel, boolean>>({
    phone: false,
    email: false,
  });
  const [otpStatus, setOtpStatus] = useState<Record<OtpChannel, OtpStatus | null>>({
    phone: null,
    email: null,
  });
  const [issuingOtp, setIssuingOtp] = useState<OtpChannel | null>(null);
  const [verifyingOtp, setVerifyingOtp] = useState<OtpChannel | null>(null);
  const [otpCooldownSeconds, setOtpCooldownSeconds] = useState<Record<OtpChannel, number>>({
    phone: 0,
    email: 0,
  });
  const phoneOtpCooldownActive = otpCooldownSeconds.phone > 0;
  const emailOtpCooldownActive = otpCooldownSeconds.email > 0;

  useEffect(() => {
    if (!phoneOtpCooldownActive && !emailOtpCooldownActive) {
      return undefined;
    }

    const timerId = window.setInterval(() => {
      setOtpCooldownSeconds((prev) => ({
        phone: Math.max(prev.phone - 1, 0),
        email: Math.max(prev.email - 1, 0),
      }));
    }, 1000);

    return () => window.clearInterval(timerId);
  }, [emailOtpCooldownActive, phoneOtpCooldownActive]);

  const getTranslatedOtpLabel = (channel: OtpChannel) =>
    channel === 'phone'
      ? t('pbl.lawyerRegistration.otp.smsOtp', { defaultValue: 'SMS OTP' })
      : t('pbl.lawyerRegistration.otp.emailOtp', { defaultValue: 'Email OTP' });

  const setChannelStatus = (channel: OtpChannel, status: OtpStatus | null) => {
    setOtpStatus((prev) => ({ ...prev, [channel]: status }));
  };

  const isOtpRequestCoolingDown = (channel: OtpChannel) => otpCooldownSeconds[channel] > 0;

  const startOtpRequestCooldown = (channel: OtpChannel, seconds = OTP_REQUEST_COOLDOWN_SECONDS) => {
    setOtpCooldownSeconds((prev) => ({
      ...prev,
      [channel]: Number.isFinite(seconds) && seconds > 0 ? Math.ceil(seconds) : OTP_REQUEST_COOLDOWN_SECONDS,
    }));
  };

  const getOtpRequestButtonLabel = (channel: OtpChannel) => {
    if (issuingOtp === channel) {
      return t('pbl.lawyerRegistration.otp.sending', { defaultValue: 'Sending...' });
    }

    const cooldownSeconds = otpCooldownSeconds[channel];

    if (cooldownSeconds > 0) {
      return t('pbl.lawyerRegistration.otp.otpVerifyCooldown', {
        seconds: cooldownSeconds,
        defaultValue: `OTP verify (${cooldownSeconds}s)`,
      });
    }

    return t('pbl.lawyerRegistration.otp.otpVerify', { defaultValue: 'OTP verify' });
  };

  const updateOtpValue = (channel: OtpChannel, value: string) => {
    if (channel === 'phone') {
      updateForm('phone_otp', value);
      return;
    }

    updateForm('email_otp', value);
    updateForm('email_verification_code', value);
  };

  const updateOtpVerified = (channel: OtpChannel, value: boolean) => {
    if (channel === 'phone') {
      updateForm('phone_otp_verified', value);
      if (!value) updateForm('phone_verification_token', '');
      return;
    }

    updateForm('email_otp_verified', value);
  };

  const getOtpValue = (channel: OtpChannel) => (channel === 'phone' ? form.phone_otp : form.email_otp);
  const isOtpVerified = (channel: OtpChannel) =>
    channel === 'phone' ? form.phone_otp_verified : form.email_otp_verified;

  const resetOtpForContactChange = (channel: OtpChannel) => {
    setOtpCooldownSeconds((prev) => ({ ...prev, [channel]: 0 }));
    if (getOtpValue(channel)) {
      updateOtpValue(channel, '');
    }

    if (isOtpVerified(channel)) {
      updateOtpVerified(channel, false);
    }

    setOtpIssued((prev) => ({ ...prev, [channel]: false }));
    if (channel === 'email') {
      updateForm('email_verification_token', '');
      updateForm('email_verification_code', '');
    }
    setChannelStatus(channel, null);
  };

  const buildPhoneOtpPayload = () => {
    const normalizedPhone = normalizeHongKongMobile(form.phone);

    if (!normalizedPhone) {
      setChannelStatus('phone', {
        tone: 'error',
        message: t('pbl.lawyerRegistration.otp.smsPhoneInvalid', {
          defaultValue: smsPhoneValidationMessage,
        }),
      });
      return null;
    }

    return {
      channel: 'phone' as const,
      ...normalizedPhone,
    };
  };

  const buildEmailOtpPayload = () => {
    const email = form.email.trim();

    if (!emailPattern.test(email)) {
      setChannelStatus('email', {
        tone: 'error',
        message: t('pbl.lawyerRegistration.otp.emailInvalidBeforeOtp', {
          defaultValue: 'Please enter a valid email address before requesting an OTP.',
        }),
      });
      return null;
    }

    return {
      channel: 'email' as const,
      email,
    };
  };

  const issueOtp = async (channel: OtpChannel) => {
    if (isOtpRequestCoolingDown(channel) || issuingOtp === channel) return;

    const phonePayload = channel === 'phone' ? buildPhoneOtpPayload() : null;
    const emailPayload = channel === 'email' ? buildEmailOtpPayload() : null;
    const payload = phonePayload || emailPayload;
    if (!payload) return;

    setIssuingOtp(channel);

    try {
      const response = phonePayload
        ? await issuePblLawyerPhoneOtp(phonePayload)
        : await issuePblLawyerApplicationEmailVerification({ email: emailPayload!.email });
      const issueResult = response.data.data as any;
      const issueFailed =
        response.data.success === false ||
        (channel === 'phone' && issueResult?.sent === false && !issueResult?.bypassed && !issueResult?.bypass_code);

      if (issueFailed) {
        throw new Error(
          response.data.message ||
            (channel === 'phone'
              ? t('pbl.lawyerRegistration.otp.issueSmsOtpFailed', { defaultValue: 'Failed to issue SMS OTP.' })
              : t('pbl.lawyerRegistration.otp.issueEmailOtpFailed', { defaultValue: 'Failed to issue Email OTP.' }))
        );
      }

      updateOtpValue(channel, '');
      updateOtpVerified(channel, false);
      if (channel === 'email') {
        updateForm('email_verification_token', issueResult?.one_time_token || '');
        updateForm('email_verification_code', '');
      }
      setOtpIssued((prev) => ({ ...prev, [channel]: true }));
      startOtpRequestCooldown(channel, Number(issueResult?.cooldown_seconds ?? OTP_REQUEST_COOLDOWN_SECONDS));
      setChannelStatus(channel, {
        tone: 'info',
        message:
          response.data.message ||
          (channel === 'phone'
            ? t('pbl.lawyerRegistration.otp.smsOtpSent', {
                defaultValue: 'SMS OTP sent. Please enter the OTP to verify.',
              })
            : t('pbl.lawyerRegistration.otp.emailOtpSent', {
                defaultValue: 'Email OTP sent. Please enter the OTP to verify.',
              })),
      });
    } catch (error: any) {
      if (error.response?.status === 429 || error.response?.status === 503) {
        const seconds = Number(error.response.headers?.['retry-after'] ?? error.response.data?.data?.retry_after);
        startOtpRequestCooldown(channel, seconds);
        const message = authRequestError(error, t);
        setChannelStatus(channel, { tone: 'error', message });
        errorHandler(message);
        return;
      }
      const message = getApiErrorMessage(
        error,
        channel === 'phone'
          ? t('pbl.lawyerRegistration.otp.issueSmsOtpFailed', { defaultValue: 'Failed to issue SMS OTP.' })
          : t('pbl.lawyerRegistration.otp.issueEmailOtpFailed', { defaultValue: 'Failed to issue Email OTP.' })
      );
      setChannelStatus(channel, { tone: 'error', message });
      errorHandler(message);
    } finally {
      setIssuingOtp(null);
    }
  };

  const verifyOtp = async (channel: OtpChannel) => {
    const otp = getOtpValue(channel).trim();

    if (!otp) {
      setChannelStatus(channel, {
        tone: 'error',
        message:
          channel === 'phone'
            ? t('pbl.lawyerRegistration.otp.smsOtpRequired', { defaultValue: 'SMS OTP is required.' })
            : t('pbl.lawyerRegistration.otp.emailOtpRequired', { defaultValue: 'Email OTP is required.' }),
      });
      return;
    }

    const contactPayload = channel === 'phone' ? buildPhoneOtpPayload() : buildEmailOtpPayload();
    if (!contactPayload) return;

    setVerifyingOtp(channel);

    try {
      if (channel === 'email') {
        if (!form.email_verification_token) {
          throw new Error('Please request an Email OTP before verifying.');
        }

        const emailVerificationPayload = contactPayload as { channel: 'email'; email: string };
        const response = await verifyPblLawyerApplicationEmailVerification({
          email: emailVerificationPayload.email,
          email_verification_token: form.email_verification_token,
          email_verification_code: otp,
        });

        if (response.data.success === false || response.data.data?.verified === false) {
          throw new Error(
            response.data.message ||
              t('pbl.lawyerRegistration.otp.verifyEmailOtpFailed', {
                defaultValue: 'Failed to verify Email OTP.',
              })
          );
        }

        updateForm('email_verification_code', otp);
        updateOtpVerified(channel, true);
        setOtpIssued((prev) => ({ ...prev, [channel]: true }));
        setChannelStatus(channel, {
          tone: 'success',
          message: t('pbl.lawyerRegistration.otp.emailOtpVerified', { defaultValue: 'Email OTP verified.' }),
        });
        return;
      }

      const response = await verifyPblLawyerPhoneOtp({
        ...contactPayload,
        otp,
      });

      if (!response.data.data?.phone_verification_token) {
        throw new Error(t('pbl.lawyerRegistration.otp.verifySmsOtpFailed', { defaultValue: 'Failed to verify SMS OTP.' }));
      }
      updateForm('phone_verification_token', response.data.data.phone_verification_token);

      if (response.data.success === false || response.data.data?.verified === false) {
        throw new Error(
          response.data.message ||
            (channel === 'phone'
              ? t('pbl.lawyerRegistration.otp.verifySmsOtpFailed', { defaultValue: 'Failed to verify SMS OTP.' })
              : t('pbl.lawyerRegistration.otp.verifyEmailOtpFailed', {
                  defaultValue: 'Failed to verify Email OTP.',
                }))
        );
      }

      updateOtpVerified(channel, true);
      setOtpIssued((prev) => ({ ...prev, [channel]: true }));
      setChannelStatus(channel, {
        tone: 'success',
        message:
          channel === 'phone'
            ? t('pbl.lawyerRegistration.otp.smsOtpVerified', { defaultValue: 'SMS OTP verified.' })
            : t('pbl.lawyerRegistration.otp.emailOtpVerified', { defaultValue: 'Email OTP verified.' }),
      });
    } catch (error: any) {
      const message = getApiErrorMessage(
        error,
        channel === 'phone'
          ? t('pbl.lawyerRegistration.otp.verifySmsOtpFailed', { defaultValue: 'Failed to verify SMS OTP.' })
          : t('pbl.lawyerRegistration.otp.verifyEmailOtpFailed', { defaultValue: 'Failed to verify Email OTP.' })
      );
      updateOtpVerified(channel, false);
      setChannelStatus(channel, { tone: 'error', message });
      errorHandler(message);
    } finally {
      setVerifyingOtp(null);
    }
  };

  const handleOtpChange = (channel: OtpChannel, event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    updateOtpValue(channel, event.target.value);

    if (isOtpVerified(channel)) {
      updateOtpVerified(channel, false);
    }

    setChannelStatus(channel, null);
  };

  const renderOtpInput = (channel: OtpChannel) => {
    const otpLabel = getTranslatedOtpLabel(channel);
    const otpFieldName = getOtpFieldName(channel);
    const verified = isOtpVerified(channel);

    if (!otpIssued[channel] && !getOtpValue(channel) && !verified) {
      return null;
    }

    return (
      <InputWrapper marginBottom={26}>
        <Flex alignItems={isMobile ? 'stretch' : 'flex-end'} flexDirection={isMobile ? 'column' : 'row'} gridGap={12}>
          <Controller
            control={control}
            name={otpFieldName}
            rules={{
              validate: (value) =>
                verified ||
                Boolean(value?.trim()) ||
                (channel === 'phone'
                  ? t('pbl.lawyerRegistration.otp.smsOtpRequired', { defaultValue: 'SMS OTP is required.' })
                  : t('pbl.lawyerRegistration.otp.emailOtpRequired', { defaultValue: 'Email OTP is required.' })),
            }}
            render={({ field, fieldState }) => (
              <Flex alignItems="flex-end" width="100%" gridGap={'5px'}>
                <Box paddingBottom={'6px'}>　</Box>
                <TextField
                  label={`${otpLabel} *`}
                  variant="standard"
                  value={field.value || ''}
                  onChange={(event) => handleOtpChange(channel, event)}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  fullWidth
                />
              </Flex>
            )}
          />
          <Button
            type="button"
            size="md"
            variant={verified ? 'primary' : 'primary-invert'}
            rounded={false}
            disabled={verified || verifyingOtp === channel}
            onClick={() => verifyOtp(channel)}
          >
            {verified
              ? t('pbl.lawyerRegistration.otp.verified', { defaultValue: 'Verified' })
              : verifyingOtp === channel
              ? t('pbl.lawyerRegistration.otp.verifying', { defaultValue: 'Verifying...' })
              : channel === 'phone'
              ? t('pbl.lawyerRegistration.otp.verifySmsOtp', { defaultValue: 'Verify SMS OTP' })
              : t('pbl.lawyerRegistration.otp.verifyEmailOtp', { defaultValue: 'Verify Email OTP' })}
          </Button>
        </Flex>
        {otpStatus[channel] && (
          <OtpStatusText tone={otpStatus[channel]!.tone}>　{otpStatus[channel]!.message}</OtpStatusText>
        )}
      </InputWrapper>
    );
  };

  return (
    <FormPanel isTablet={isTablet}>
      <SectionTitle>
        {t('pbl.lawyerRegistration.personalInformation.section', { defaultValue: 'Section 1' })}
      </SectionTitle>
      <FormSectionTitle>
        {t('pbl.lawyerRegistration.personalInformation.title', { defaultValue: 'Personal Information' })}
      </FormSectionTitle>
      {validationError && <ValidationText>{validationError}</ValidationText>}

      <MultiInputWrapper style={{ height: 'auto' }}>
        <Controller
          control={control}
          name="surname_en"
          rules={{
            required: t('pbl.lawyerRegistration.personalInformation.errors.surnameEnRequired', {
              defaultValue: 'Surname (English) is required.',
            }),
          }}
          render={({ field, fieldState }) => (
            <Flex flexDirection="column" gridGap={'5px'} width="100%">
              <Flex alignItems="flex-end" width="100%" gridGap={'5px'}>
                <Box paddingBottom={'6px'}>1.</Box>
                <TextField
                  label={t('pbl.lawyerRegistration.personalInformation.fields.surnameEn', {
                    defaultValue: 'Surname (English) *',
                  })}
                  variant="standard"
                  value={field.value || ''}
                  onChange={(event) => updateForm('surname_en', event.target.value)}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  fullWidth
                />
              </Flex>
              <Box paddingLeft="8px">
                <Text fontSize={12} color={theme.colors.gray400}>
                  {t('pbl.lawyerRegistration.personalInformation.helpText.nameAsShown', {
                    defaultValue: 'As shown on your practising certificate or registered trainee solicitor contract',
                  })}
                </Text>
              </Box>
            </Flex>
          )}
        />
        <Controller
          control={control}
          name="given_name_en"
          rules={{
            required: t('pbl.lawyerRegistration.personalInformation.errors.givenNameEnRequired', {
              defaultValue: 'Given Name (English) is required.',
            }),
          }}
          render={({ field, fieldState }) => (
            <Flex flexDirection="column" gridGap={'5px'} width="100%">
              <Flex alignItems="flex-end" width="100%" gridGap={'5px'}>
                <Box paddingBottom={'6px'}>&nbsp;</Box>
                <TextField
                  label={t('pbl.lawyerRegistration.personalInformation.fields.givenNameEn', {
                    defaultValue: 'Given Name (English) *',
                  })}
                  variant="standard"
                  value={field.value || ''}
                  onChange={(event) => updateForm('given_name_en', event.target.value)}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  fullWidth
                />
              </Flex>
              <Box paddingLeft="8px">
                <Text fontSize={12} color={theme.colors.gray400}>
                  {t('pbl.lawyerRegistration.personalInformation.helpText.nameAsShown', {
                    defaultValue: 'As shown on your practising certificate or registered trainee solicitor contract',
                  })}
                </Text>
              </Box>
            </Flex>
          )}
        />
      </MultiInputWrapper>

      <MultiInputWrapper style={{ height: 'auto' }}>
        <Controller
          control={control}
          name="surname_zh"
          rules={{
            required: t('pbl.lawyerRegistration.personalInformation.errors.surnameZhRequired', {
              defaultValue: 'Surname (Chinese) is required.',
            }),
          }}
          render={({ field, fieldState }) => (
            <Flex flexDirection="column" gridGap={'5px'} width="100%">
              <Flex alignItems="flex-end" width="100%" gridGap={'5px'}>
                <Box paddingBottom={'6px'}>　</Box>
                <TextField
                  label={t('pbl.lawyerRegistration.personalInformation.fields.surnameZh', {
                    defaultValue: 'Surname (Chinese) *',
                  })}
                  variant="standard"
                  value={field.value || ''}
                  onChange={(event) => updateForm('surname_zh', event.target.value)}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  fullWidth
                />
              </Flex>
              <Box paddingLeft="8px">
                <Text fontSize={12} color={theme.colors.gray400}>
                  {t('pbl.lawyerRegistration.personalInformation.helpText.nameAsShown', {
                    defaultValue: 'As shown on your practising certificate or registered trainee solicitor contract',
                  })}
                </Text>
              </Box>
            </Flex>
          )}
        />
        <Controller
          control={control}
          name="given_name_zh"
          rules={{
            required: t('pbl.lawyerRegistration.personalInformation.errors.givenNameZhRequired', {
              defaultValue: 'Given Name (Chinese) is required.',
            }),
          }}
          render={({ field, fieldState }) => (
            <Flex flexDirection="column" gridGap={'5px'} width="100%">
              <Flex alignItems="flex-end" width="100%" gridGap={'5px'}>
                <Box paddingBottom={'6px'}>&nbsp;</Box>
                <TextField
                  label={t('pbl.lawyerRegistration.personalInformation.fields.givenNameZh', {
                    defaultValue: 'Given Name (Chinese) *',
                  })}
                  variant="standard"
                  value={field.value || ''}
                  onChange={(event) => updateForm('given_name_zh', event.target.value)}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  fullWidth
                />
              </Flex>
              <Box paddingLeft="8px">
                <Text fontSize={12} color={theme.colors.gray400}>
                  {t('pbl.lawyerRegistration.personalInformation.helpText.nameAsShown', {
                    defaultValue: 'As shown on your practising certificate or registered trainee solicitor contract',
                  })}
                </Text>
              </Box>
            </Flex>
          )}
        />
      </MultiInputWrapper>

      {showAccountFields && (
        <>
          <InputWrapper>
            <Flex
              alignItems={isMobile ? 'stretch' : 'flex-end'}
              flexDirection={isMobile ? 'column' : 'row'}
              gridGap={12}
            >
              <Controller
                control={control}
                name="phone"
                rules={{
                  required: t('pbl.lawyerRegistration.personalInformation.errors.phoneRequired', {
                    defaultValue: 'Phone Number is required.',
                  }),
                  pattern: {
                    value: phonePattern,
                    message: t('pbl.lawyerRegistration.personalInformation.errors.phoneInvalid', {
                      defaultValue: 'Please enter a valid phone number.',
                    }),
                  },
                }}
                render={({ field, fieldState }) => (
                  <Flex alignItems="flex-end" width="100%" gridGap={'5px'}>
                    <Box paddingBottom={'6px'}>2.</Box>
                    <TextField
                      label={t('pbl.lawyerRegistration.personalInformation.fields.phone', {
                        defaultValue: 'Phone Number *',
                      })}
                      disabled={issuingOtp === 'phone' || verifyingOtp === 'phone'}
                      variant="standard"
                      value={field.value}
                      onChange={(event) => {
                        field.onChange(event);
                        resetOtpForContactChange('phone');
                      }}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      fullWidth
                    />
                  </Flex>
                )}
              />
              <Button
                type="button"
                size="md"
                variant="primary-invert"
                rounded={false}
                disabled={!form.phone?.trim() || issuingOtp === 'phone' || isOtpRequestCoolingDown('phone')}
                onClick={() => issueOtp('phone')}
              >
                {getOtpRequestButtonLabel('phone')}
              </Button>
            </Flex>
            {otpStatus.phone && !otpIssued.phone && (
              <OtpStatusText tone={otpStatus.phone.tone}>{otpStatus.phone.message}</OtpStatusText>
            )}
            {renderOtpInput('phone')}
          </InputWrapper>

          <InputWrapper>
            <Flex
              alignItems={isMobile ? 'stretch' : 'flex-end'}
              flexDirection={isMobile ? 'column' : 'row'}
              gridGap={12}
            >
              <Controller
                control={control}
                name="email"
                rules={{
                  required: t('pbl.lawyerRegistration.personalInformation.errors.emailRequired', {
                    defaultValue: 'Email Address is required.',
                  }),
                  pattern: {
                    value: emailPattern,
                    message: t('pbl.lawyerRegistration.personalInformation.errors.emailInvalid', {
                      defaultValue: 'Please enter a valid email address.',
                    }),
                  },
                }}
                render={({ field, fieldState }) => (
                  <Flex alignItems="flex-end" width="100%" gridGap={'5px'}>
                    <Box paddingBottom={'6px'}>3.</Box>
                    <TextField
                      label={t('pbl.lawyerRegistration.personalInformation.fields.email', {
                        defaultValue: 'Email Address *',
                      })}
                      disabled={issuingOtp === 'email' || verifyingOtp === 'email'}
                      variant="standard"
                      value={field.value}
                      onChange={(event) => {
                        field.onChange(event);
                        resetOtpForContactChange('email');
                      }}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      fullWidth
                    />
                  </Flex>
                )}
              />
              <Button
                type="button"
                size="md"
                variant="primary-invert"
                rounded={false}
                disabled={!form.email?.trim() || issuingOtp === 'email' || isOtpRequestCoolingDown('email')}
                onClick={() => issueOtp('email')}
              >
                {getOtpRequestButtonLabel('email')}
              </Button>
            </Flex>
            {otpStatus.email && !otpIssued.email && (
              <OtpStatusText tone={otpStatus.email.tone}>{otpStatus.email.message}</OtpStatusText>
            )}
            {renderOtpInput('email')}
          </InputWrapper>

          <InputWrapper>
            <Controller
              control={control}
              name="password"
              rules={{
                required: t('pbl.lawyerRegistration.personalInformation.errors.passwordRequired', {
                  defaultValue: 'Password is required.',
                }),
                pattern: {
                  value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{9,}$/,
                  message: t('pbl.lawyerRegistration.personalInformation.helpText.passwordRule', {
                    defaultValue:
                      'At least 9 characters, including 1 uppercase letter, 1 lowercase letter, 1 number, and 1 special character.',
                  }),
                },
              }}
              render={({ field, fieldState }) => (
                <Flex flexDirection="column" gridGap={'5px'} width="100%">
                  <Flex alignItems="flex-end" width="100%" gridGap={'5px'}>
                    <Box paddingBottom={'6px'}>　</Box>
                    <TextField
                      label={t('pbl.lawyerRegistration.personalInformation.fields.password', {
                        defaultValue: 'Password *',
                      })}
                      variant="standard"
                      type={showPassword ? 'text' : 'password'}
                      value={field.value}
                      onChange={field.onChange}
                      error={!!fieldState.error}
                      helperText={
                        fieldState.error?.message ||
                        t('pbl.lawyerRegistration.personalInformation.helpText.passwordRule', {
                          defaultValue:
                            'At least 9 characters, including 1 uppercase letter, 1 lowercase letter, 1 number, and 1 special character.',
                        })
                      }
                      fullWidth
                    />
                    <IconWrapper onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <Visible.VisibleSVG /> : <Visible.VisibleOffSVG />}
                    </IconWrapper>
                  </Flex>
                </Flex>
              )}
            />
          </InputWrapper>

          <InputWrapper>
            <Controller
              control={control}
              name="password_confirmation"
              rules={{
                required: t('pbl.lawyerRegistration.personalInformation.errors.confirmPasswordRequired', {
                  defaultValue: 'Please re-enter your password.',
                }),
                validate: (value) =>
                  value === form.password ||
                  t('pbl.lawyerRegistration.personalInformation.errors.passwordMismatch', {
                    defaultValue: 'Password confirmation does not match.',
                  }),
              }}
              render={({ field, fieldState }) => (
                <Flex alignItems="flex-end" width="100%" gridGap={'5px'}>
                  <Box paddingBottom={'6px'}>　</Box>
                  <TextField
                    label={t('pbl.lawyerRegistration.personalInformation.fields.confirmPassword', {
                      defaultValue: 'Re-enter Password *',
                    })}
                    variant="standard"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={field.value}
                    onChange={field.onChange}
                    error={!!fieldState.error}
                    helperText={
                      fieldState.error?.message ||
                      t('pbl.lawyerRegistration.personalInformation.helpText.passwordMatch', {
                        defaultValue: 'Please ensure both passwords match.',
                      })
                    }
                    fullWidth
                  />
                  <IconWrapper onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                    {showConfirmPassword ? <Visible.VisibleSVG /> : <Visible.VisibleOffSVG />}
                  </IconWrapper>
                </Flex>
              )}
            />
          </InputWrapper>
        </>
      )}

      <InputWrapper marginBottom={26}>
        <Controller
          control={control}
          name="hk_solicitor_admission_year"
          rules={{
            required: t('pbl.lawyerRegistration.personalInformation.errors.admissionYearRequired', {
              defaultValue: 'Admission year is required.',
            }),
            pattern: {
              value: /^\d{4}$/,
              message: t('pbl.lawyerRegistration.personalInformation.errors.admissionYearFourDigits', {
                defaultValue: 'Please enter a 4-digit year.',
              }),
            },
            validate: (value) => {
              const year = Number(value);
              const currentYear = new Date().getFullYear();
              return (
                (year >= 1900 && year <= currentYear) ||
                t('pbl.lawyerRegistration.personalInformation.errors.admissionYearInvalid', {
                  defaultValue: 'Please enter a valid admission year.',
                })
              );
            },
          }}
          render={({ field, fieldState }) => (
            <Flex alignItems="flex-end" width="100%" gridGap={'5px'}>
              <Box paddingBottom={'6px'}>{admissionQuestionNumber}</Box>
              <TextField
                label={t('pbl.lawyerRegistration.personalInformation.fields.admissionYear', {
                  defaultValue: 'Year of Admission as a Hong Kong Solicitor *',
                })}
                variant="standard"
                name={field.name}
                inputRef={field.ref}
                value={field.value}
                onChange={(event) => updateForm(field.name, event.target.value)}
                onBlur={field.onBlur}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                fullWidth
              />
            </Flex>
          )}
        />
      </InputWrapper>

      <InputWrapper marginBottom={20}>
        <Flex gridGap={'5px'}>
          {qualificationQuestionNumber}
          <FormTitle marginBottom={10}>
            {t('pbl.lawyerRegistration.personalInformation.fields.otherProfessionalQualifications', {
              defaultValue: 'Other Professional Qualifications *',
            })}
          </FormTitle>
        </Flex>
        <ToggleGroup
          name="other_professional_qualifications"
          options={pblProfessionalQualifications.map((item) => ({
            value: item.value,
            label: t(item.labelKey as any, { defaultValue: item.defaultLabel }) as string,
          }))}
          values={form.other_professional_qualifications || []}
          onChange={(value) => updateForm('other_professional_qualifications', value)}
        />
        {errors.other_professional_qualifications?.message && (
          <ValidationText>{errors.other_professional_qualifications.message}</ValidationText>
        )}
      </InputWrapper>

      {showQualificationOther && (
        <InputWrapper marginBottom={26}>
          <TextField
            label={t('pbl.lawyerRegistration.personalInformation.fields.pleaseSpecify', {
              defaultValue: 'Please specify',
            })}
            variant="standard"
            value={form.other_professional_qualification_text || ''}
            onChange={(event) => updateForm('other_professional_qualification_text', event.target.value)}
            error={!!errors.other_professional_qualification_text}
            helperText={errors.other_professional_qualification_text?.message}
            fullWidth
            placeholder={t('pbl.lawyerRegistration.personalInformation.fields.pleaseSpecify', {
              defaultValue: 'Please specify',
            })}
          />
        </InputWrapper>
      )}

      <InputWrapper>
        <Flex gridGap={'5px'}>
          {languageQuestionNumber}
          <FormTitle marginBottom={10}>
            {t('pbl.lawyerRegistration.personalInformation.fields.languageProficiency', {
              defaultValue: 'Language Proficiency *',
            })}
          </FormTitle>
        </Flex>
        <ToggleGroup
          name="language_proficiency"
          options={pblLanguages.map((item) => ({
            value: item.value,
            label: t(item.labelKey as any, { defaultValue: item.defaultLabel }) as string,
          }))}
          values={form.language_proficiency}
          onChange={(value) => updateForm('language_proficiency', value)}
        />
        {errors.language_proficiency?.message && <ValidationText>{errors.language_proficiency.message}</ValidationText>}
      </InputWrapper>

      {showLanguageOther && (
        <InputWrapper marginBottom={0}>
          <TextField
            label={t('pbl.lawyerRegistration.personalInformation.fields.otherLanguage', {
              defaultValue: 'Other language',
            })}
            variant="standard"
            value={form.language_other_text || ''}
            onChange={(event) => updateForm('language_other_text', event.target.value)}
            placeholder={t('pbl.lawyerRegistration.personalInformation.fields.pleaseSpecify', {
              defaultValue: 'Please specify',
            })}
            fullWidth
          />
        </InputWrapper>
      )}
    </FormPanel>
  );
};

export default PersonalInformationStep;
